'use client'

import { cn } from '@/lib/utils'
import { useStoreI18n } from '@/store'
import { Input as InputShadCn } from '@/ui/Shadcn/input'
import { Label } from '@/ui/Shadcn/label'
import { Button, ButtonIcon, InputFake, Popover, Select } from '@/ui/components'
import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'

interface InputProps extends React.ComponentProps<typeof InputShadCn> {
  label?: string
  className?: string
  id?: string
  placeholder?: string
}

const DEFAULT_GUEST_VALUE = {
  adults: 2,
  childrens: 0,
  childrensAges: [],
  rooms: 1
}

const InputNumber = ({
  value,
  min = 0,
  max = 20,
  onChange,
  label,
  id
}: {
  value?: number
  min?: number
  max?: number
  onChange?: (value: number) => void
  label?: string
  id?: string
}) => {
  const currentValue = Number(value) || 0

  const handleChange = (nextValue: number) => {
    onChange?.(nextValue)
  }

  return (
    <div className="gap-xs flex justify-between">
      <Label htmlFor={id}>{label}</Label>
      <div className="gap-xs flex items-center">
        <ButtonIcon
          size="s"
          variant="filled"
          onClick={() =>
            handleChange(currentValue > min ? currentValue - 1 : min)
          }
          icon="material-symbols:remove"
        />
        <div className="textstyle-md-regular w-4 appearance-none text-center">
          {currentValue}
        </div>
        <ButtonIcon
          size="s"
          variant="filled"
          onClick={() =>
            handleChange(currentValue < max ? currentValue + 1 : max)
          }
          icon="material-symbols:add"
        />
      </div>
    </div>
  )
}

const InputGuest = ({
  label,
  className,
  id,
  placeholder,
  ...props
}: InputProps) => {
  const [value, setValue] = useState<{
    adults: number
    childrens: number
    childrensAges: number[]
    rooms: number
  }>(DEFAULT_GUEST_VALUE)

  const [popoverOpen, setPopoverOpen] = useState(false)
  const [formattedValue, setFormattedValue] = useState<string | null>(null)
  const D = useStoreI18n.use.dictionary()
  const [childrenAgesHeight, setChildrenAgesHeight] = useState(0)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const childrenAgesRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        // We only have one entry, so we can use entries[0].
        const observedHeight = entries?.[0]?.contentRect?.height
        setChildrenAgesHeight(observedHeight ?? 'auto')
      })
      resizeObserverRef.current.observe(node)
    } else if (resizeObserverRef.current) {
      // Disconnect the observer when the node is unmounted to prevent memory leaks
      resizeObserverRef.current.disconnect()
    }
  }, [])

  const handleChildrensAgesChange = (index: number, childAge: number) => {
    setValue((prev) => {
      const childrensAges = [...prev.childrensAges]
      childrensAges[index] = childAge

      return { ...prev, childrensAges }
    })
  }

  const handleChildrensChange = (childrens: number) => {
    setValue((prev) => {
      const childrensAges = [...prev.childrensAges]

      if (childrensAges.length > childrens) {
        childrensAges.length = childrens
      }

      while (childrensAges.length < childrens) {
        childrensAges.push(0)
      }

      return { ...prev, childrens, childrensAges }
    })
  }

  const isDefaultValue = value === DEFAULT_GUEST_VALUE

  useEffect(() => {
    let formattedValue = null

    formattedValue = `${value?.adults} ${D['fe.booking.adults']}${value?.childrens > 0 ? `, ${value?.childrens} ${D['fe.booking.children']}` : ''}`

    setTimeout(() => {
      setFormattedValue(formattedValue)
    }, 0)
  })

  return (
    <>
      <Popover
        side="top"
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        triggerClassName={'input-focus'}
        button={
          <InputFake
            active={popoverOpen}
            label={label}
            value={formattedValue || ''}
            placeholder={placeholder}
          />
        }
      >
        <div className={cn(`gap-xs relative flex flex-col`, className)}>
          <InputNumber
            id={`number-adults`}
            label={D['fe.bookingbar.adults']}
            value={value?.adults}
            onChange={(adults) => setValue((prev) => ({ ...prev, adults }))}
          />
          <motion.div
            style={{ height: childrenAgesHeight }}
            animate={{ height: childrenAgesHeight }}
            transition={{ duration: 0.25 }}
          >
            <div ref={childrenAgesRef} className="gap-xs flex flex-col">
              <InputNumber
                id={`number-children`}
                label={D['fe.bookingbar.children']}
                value={value?.childrens}
                onChange={(value) => handleChildrensChange(value)}
              />
              <AnimatePresence initial={false}>
                {value?.childrens > 0 &&
                  Array.from({ length: value?.childrens }, (_, index) => (
                    <motion.div
                      key={`children-ages-${index.toString()}`}
                      initial={{ opacity: 0, scale: 0.4 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.25 }}
                    >
                      <Select
                        id={`children-ages-${index.toString()}`}
                        label={D['fe.bookingbar.childrenAge']}
                        size="s"
                        value={String(value.childrensAges[index] ?? 0)}
                        onValueChange={(childAge) =>
                          handleChildrensAgesChange(index, Number(childAge))
                        }
                        options={Array.from({ length: 18 }, (_, index) => ({
                          value: index.toString(),
                          label: `${index} ${index === 1 ? D['fe.bookingbar.year'] || 'year' : D['fe.bookingbar.years'] || 'years'}`
                        }))}
                      />
                    </motion.div>
                  ))}
              </AnimatePresence>
            </div>
          </motion.div>
          <InputNumber
            id={`${id}-rooms`}
            label={D['fe.bookingbar.room']}
            min={1}
            value={value?.rooms}
            onChange={(rooms) => setValue((prev) => ({ ...prev, rooms }))}
          />

          <div className="gap-xs mt-sm flex flex-col">
            <Button onClick={() => setPopoverOpen(false)}>
              {D['fe.bookingbar.apply']}
            </Button>
            <AnimatePresence initial={false}>
              {!isDefaultValue && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  transition={{ duration: 0.25 }}
                  className="flex flex-col overflow-hidden"
                >
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setValue(DEFAULT_GUEST_VALUE)
                    }}
                  >
                    {D['fe.bookingbar.clear']}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Popover>
      <input
        id={id}
        type="hidden"
        value={value != null ? JSON.stringify(value) : ''}
        {...props}
      />
      <input type="hidden" name={'number_adult'} value={value?.adults} />
      <input type="hidden" name={'number_child'} value={value?.childrens} />
      <input type="hidden" name={'number_rooms'} value={value?.rooms} />
      {value?.childrensAges.length > 0 &&
        value?.childrensAges.map((age, index) => (
          <input
            key={`children-ages-${index.toString()}`}
            type="hidden"
            name={`age_child${index + 1}`}
            value={age.toString()}
          />
        ))}
    </>
  )
}

InputGuest.displayName = 'InputGuest'

export default InputGuest
